import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { Plus, Pencil, Trash2, Syringe } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
const chip = (color) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`;
const STATUS_TONE = {
  Completed: "bg-[#E8F5E9] text-[#2E7D32]",
  Refused: "bg-[#FFEBEE] text-[#C62828]",
  Contraindicated: "bg-[#FFEBEE] text-[#C62828]",
  "Not Given": "bg-[#FFF3E0] text-[#8D5A00]",
  "Series In Progress": "bg-[#E3F2FD] text-[#1565C0]",
};

const UNITS = ["mL", "mg", "mcg", "IU", "Dose"];
const FORMS = ["Injection", "Oral", "Nasal Spray", "Intradermal", "Other"];
const ROUTES = ["Intramuscular (IM)", "Subcutaneous (SC)", "Intradermal (ID)", "Oral (PO)", "Nasal", "Intravenous (IV)"];
const SITES = ["Left Deltoid", "Right Deltoid", "Left Thigh", "Right Thigh", "Left Gluteus", "Right Gluteus", "Other"];

const EMPTY = { vaccine: "", cvx: "", date: dayjs().format("YYYY-MM-DD"), time: "", administered_by: "",
                amount: "", units: "mL", form: "Injection", route: "Intramuscular (IM)", site: "",
                doses: "1", dose_of: "", lot_number: "", manufacturer: "", manuf_id: "", status: "Completed",
                reason_not_completed: "", next_dose_due: "", vis_given_date: "", vis_publication_date: "",
                reaction_to_prior_dose: "", contraindications: "", notes: "" };

export default function ImmunizationList({ client, editable, dict }) {
  const vaccines = dict?.vaccines || [];
  const statuses = dict?.immunization_statuses || ["Completed", "Refused", "Contraindicated", "Not Given", "Series In Progress"];
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const load = () => api.get(`/clients/${client.id}/immunizations`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line
  const filtered = useMemo(() => items.filter((i) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [i.vaccine, i.cvx, i.manufacturer, i.lot_number, i.status].some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vaccine.trim()) return toast.error("Vaccine name is required");
    try {
      if (editId) await api.put(`/immunizations/${editId}`, form);
      else await api.post(`/clients/${client.id}/immunizations`, form);
      toast.success(editId ? "Immunization updated" : "Immunization added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/immunizations/${del.id}`); toast.success("Immunization removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="immunization-list-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">All Immunizations List</h3>
          <p className="text-xs text-[#7B6157]">{filtered.length} entr{filtered.length === 1 ? "y" : "ies"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="imm-filter" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
          {editable && <Btn data-testid="add-imm-btn" variant="outline"
            onClick={() => { setForm(EMPTY); setEditId(null); setPanel(true); }}><Plus size={14} /> Add New Immunizations</Btn>}
        </div>
      </div>

      {filtered.length === 0 ? <Empty text="No immunizations on file" /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FDFCF8]">
              <tr className="text-left text-[10px] font-bold uppercase text-[#7B6157]">
                {["Date","Immunization","CVX","Amount","Units","Form","Route","Doses","Lot No","Manufacturer","Manuf ID","Status","Next Due","Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 whitespace-nowrap ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`imm-row-${i.id}`} className="border-t border-[#EFEBE0]">
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(i.date)}</td>
                  <td className="px-3 py-2 font-semibold text-[#3E2723] whitespace-nowrap"><Syringe size={13} className="inline mr-1 text-[#7B6157]" />{i.vaccine}</td>
                  <td className="px-3 py-2 font-mono text-[13px] text-[#7B6157]">{i.cvx || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.amount || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.units || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.form || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.route || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.doses || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.lot_number || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.manufacturer || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{i.manuf_id || "-"}</td>
                  <td className="px-3 py-2"><span className={chip(STATUS_TONE[i.status] || STATUS_TONE.Completed)}>{i.status || "Completed"}</span></td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(i.next_dose_due)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <>
                          <button data-testid={`edit-imm-${i.id}`} onClick={() => { setForm({ ...EMPTY, ...i }); setEditId(i.id); setPanel(true); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                          <button data-testid={`delete-imm-${i.id}`} onClick={() => setDel(i)}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Immunization" : "Add New Immunizations"} testId="imm-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Immunization *</FLabel><FSelect data-testid="imm-vaccine" value={form._vaccine_key || (form.vaccine ? `custom__${form.vaccine}` : "")}
              placeholder="Select vaccine…"
              onChange={(e) => {
                const v = e.target.value;
                if (!v) { setForm((s) => ({ ...s, _vaccine_key: "", vaccine: "", cvx: "" })); return; }
                if (v === "__custom__") { setForm((s) => ({ ...s, _vaccine_key: "__custom__", vaccine: "" })); return; }
                const [cvx, name] = v.split("|");
                const found = vaccines.find((x) => x.cvx === cvx && x.name === name);
                setForm((s) => ({ ...s, _vaccine_key: v, vaccine: name, cvx, manufacturer: found?.manufacturer || s.manufacturer }));
              }}>
              {vaccines.map((v) => <option key={`${v.cvx}|${v.name}`} value={`${v.cvx}|${v.name}`}>{v.name} · CVX {v.cvx} ({v.manufacturer})</option>)}
              <option value="__custom__">Custom / Other…</option>
            </FSelect></div>
            {form._vaccine_key === "__custom__" && <div><FLabel>Custom Name</FLabel><FInput data-testid="imm-vaccine-custom" value={form.vaccine} onChange={(e) => setForm((s) => ({ ...s, vaccine: e.target.value }))} placeholder="Enter vaccine" /></div>}
            <div><FLabel>CVX Code</FLabel><FInput data-testid="imm-cvx" value={form.cvx} onChange={f("cvx")} /></div>
            <div><FLabel>Onset Date *</FLabel><DateField testId="imm-date" value={form.date} onChange={(v) => setForm((s) => ({ ...s, date: v }))} /></div>
            <div><FLabel>Time</FLabel><FInput data-testid="imm-time" value={form.time} onChange={f("time")} placeholder="10:30" /></div>
            <div><FLabel>Administered By</FLabel><FInput data-testid="imm-by" value={form.administered_by} onChange={f("administered_by")} placeholder="RN Torres" /></div>
            <div><FLabel>Amount</FLabel><FInput data-testid="imm-amount" value={form.amount} onChange={f("amount")} placeholder="0.5" /></div>
            <div><FLabel>Units</FLabel><FSelect data-testid="imm-units" value={form.units} onChange={f("units")}>{UNITS.map((u) => <option key={u}>{u}</option>)}</FSelect></div>
            <div><FLabel>Form</FLabel><FSelect data-testid="imm-form" value={form.form} onChange={f("form")}>{FORMS.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Route</FLabel><FSelect data-testid="imm-route" value={form.route} onChange={f("route")}>{ROUTES.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Site</FLabel><FSelect data-testid="imm-site" value={form.site} onChange={f("site")} placeholder="Select site…">{SITES.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Doses (this dose #)</FLabel><FInput data-testid="imm-doses" value={form.doses} onChange={f("doses")} placeholder="1" /></div>
            <div><FLabel>Lot No.</FLabel><FInput data-testid="imm-lot" value={form.lot_number} onChange={f("lot_number")} /></div>
            <div><FLabel>Manufacturer</FLabel><FInput data-testid="imm-manuf" value={form.manufacturer} onChange={f("manufacturer")} /></div>
            <div><FLabel>Manuf ID</FLabel><FInput data-testid="imm-manufid" value={form.manuf_id} onChange={f("manuf_id")} /></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="imm-status" value={form.status} onChange={f("status")}>{statuses.map((x) => <option key={x}>{x}</option>)}</FSelect></div>
            <div><FLabel>Next Dose Due</FLabel><DateField testId="imm-next" value={form.next_dose_due} onChange={(v) => setForm((s) => ({ ...s, next_dose_due: v }))} /></div>
          </div>
          {(form.status === "Refused" || form.status === "Contraindicated" || form.status === "Not Given") && (
            <div><FLabel>Reason (if not completed)</FLabel><FInput data-testid="imm-reason" value={form.reason_not_completed} onChange={f("reason_not_completed")} /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Dose N of M (e.g., 1 of 3)</FLabel><FInput data-testid="imm-dose-of" value={form.dose_of} onChange={f("dose_of")} placeholder="1 of 3" /></div>
            <div><FLabel>VIS Given On</FLabel><DateField testId="imm-vis-given" value={form.vis_given_date} onChange={(v) => setForm((s) => ({ ...s, vis_given_date: v }))} /></div>
            <div><FLabel>VIS Publication Date</FLabel><DateField testId="imm-vis-pub" value={form.vis_publication_date} onChange={(v) => setForm((s) => ({ ...s, vis_publication_date: v }))} /></div>
            <div className="col-span-2"><FLabel>Reaction to Prior Dose</FLabel><FInput data-testid="imm-prior-reaction" value={form.reaction_to_prior_dose} onChange={f("reaction_to_prior_dose")} placeholder="e.g., Injection-site swelling" /></div>
            <div className="col-span-2"><FLabel>Contraindications Noted</FLabel><FInput data-testid="imm-contra" value={form.contraindications} onChange={f("contraindications")} placeholder="e.g., Egg allergy, immunocompromised, pregnancy" /></div>
          </div>
          <div><FLabel>Comments</FLabel><FTextarea data-testid="imm-comments" rows={2} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="imm-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={remove}
        title="Remove immunization" description={`Delete ${del?.vaccine}?`} />
    </div>
  );
}
